<template>
  <div
    ref="rootRef"
    v-ripple="ripple"
    class="sc-item"
    :class="classes"
    :tabindex="tabIndex"
    role="tab"
    :aria-selected="isActive ? 'true' : 'false'"
    :aria-disabled="disable ? 'true' : null"
    v-bind="$attrs"
    @click="onClick"
    @keydown="onKeydown"
  >
    <div
      ref="blurTargetRef"
      class="sc-item--focus-helper"
      tabindex="-1"
    />
    <div
      ref="tabIndicatorRef"
      class="sc-item--indicator"
      :class="[$tabs.tabProps.value.indicatorClass]"
    />
    <div
      class="sc-item--content"
      :class="innerClass"
    >
      <q-icon
        v-if="icon"
        class="sc-item--icon"
        :name="icon"
      />
      <slot>
        <div
          v-if="label"
          class="sc-item--label"
        >
          {{ label }}
        </div>
      </slot>
      <template v-if="alert">
        <q-icon
          v-if="alertIcon"
          class="sc-item--alert-icon"
          :color="alert !== true ? alert : null"
          :name="alertIcon"
        />
        <div
          v-else
          class="sc-item--alert"
          :class="alert !== true ? `text-${alert}` : null"
        />
      </template>
    </div>
  </div>
</template>

<script setup>
import { useId, inject, ref, computed, onBeforeUnmount, onMounted } from 'vue'
import { cx } from '@/helpers'

defineOptions({ name: 'Segment' })
const emit = defineEmits(['click', 'keydown'])
const props = defineProps({
  icon: { type: String, default: null },
  label: { type: [Number, String], default: null },
  alert: { type: [Boolean, String], default: false },
  alertIcon: { type: String, default: null },
  name: { type: [Number, String], default: () => `t_${useId()}` },
  tabindex: { type: [String, Number], default: null },
  disable: { type: [Boolean, String], default: false },
  contentClass: { type: String, default: null },
  ripple: { type: [Boolean, Object], default: true }
})

const $tabs = inject('segmentedTabs', null)
if ($tabs === null) {
  console.error('Segment component must be a child of SegmentedControl')
}

const navKeys = ['Home', 'End', 'ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown']

const blurTargetRef = ref(null)
const rootRef = ref(null)
const tabIndicatorRef = ref(null)

const tabData = {
  name: computed(() => props.name),
  rootRef,
  tabIndicatorRef
}

const ripple = computed(() => (
  props.disable || props.ripple
    ? false
    : Object.assign(
        { keyCodes: ['Enter', 13, 32], early: true },
        props.ripple ? {} : props.ripple
      )
))

const isActive = computed(() => $tabs.currentModel.value === props.name)

const classes = computed(() => {
  const { activeClass, activeColor, activeBgColor } = $tabs.tabProps.value
  return cx(
    isActive.value
      ? [
          'is-active',
          activeClass,
          activeColor && `text-${activeColor}`,
          activeBgColor && `bg-${activeBgColor}`
        ]
      : 'is-inactive',
    props.icon && props.label && !$tabs.tabProps.value.inlineLabel && 'is-full',
    props.disable ? 'is-disabled' : 'is-focusable is-hoverable'
  )
})

const innerClass = computed(() => cx(
  $tabs.tabProps.value.inlineLabel ? 'is-inline' : 'is-stack',
  props.contentClass
))

const tabIndex = computed(() => (
  (props.disable || $tabs.hasFocus.value || (isActive.value === false && $tabs.hasActiveTab.value))
    ? -1
    : props.tabindex ?? 0
))

const onClick = (e, kbd) => {
  if (!kbd && !e?.qAvoidFocus) {
    blurTargetRef.value?.focus()
  }
  if (props.disable === true) return
  $tabs.updateModel({ name: props.name, setCurrent: true })
  emit('click', e)
}

const shouldIgnoreKey = e => (
  e !== Object(e)
  || e.isComposing === true
  || e.qKeyEvent === true
)

const stopAndPrevent = e => {
  e.cancelable && e.preventDefault()
  e.stopPropagation()
}

const onKeydown = e => {
  if (['Enter', ' '].includes(e.key)) {
    onClick(e, true)
    return
  }
  if (
    !shouldIgnoreKey(e)
    && navKeys.includes(e.key)
    && !e.altKey
    && !e.metaKey
  ) {
    if ($tabs.onKbdNavigate(e.key, rootRef.value) === true) stopAndPrevent(e)
  }
  emit('keydown', e)
}

onMounted(() => $tabs.registerTab(tabData))
onBeforeUnmount(() => $tabs.unregisterTab(tabData))
</script>

<style scoped>
/* Styles live in SegmentedControl.vue as :deep() rules so RouteSegment shares them. */
</style>
